import { apiRequest } from '../../shared/api/client';
import { ApiError } from '../../shared/api/problem';

/**
 * Wire-format types + calls for the IT-API-Claude auth endpoints. Mirror
 * exactly Invetrak.Api.Endpoints.AuthEndpoints / MfaEndpoints. Casing matches
 * the server (camelCase via System.Text.Json defaults).
 *
 *   POST /api/v1/auth/login        → 200 UserDto | 401 | 401 mfa-required | 423 locked
 *   POST /api/v1/auth/mfa/verify         → 204 | 401 | 423
 *   POST /api/v1/auth/mfa/resend         → 204 | 401 | 503
 *   POST /api/v1/auth/mfa/recovery       → 204 | 401 | 423
 *   GET  /api/v1/auth/mfa/recovery-codes → 200 { remaining }
 *   POST /api/v1/auth/mfa/recovery-codes → 200 { recoveryCodes } (once)
 *   POST /api/v1/auth/logout       → 204 (auth required)
 *   GET  /api/v1/auth/me           → 200 UserDto | 401
 *   PATCH /api/v1/auth/me/preferences → 200 UserDto | 400 | 401
 */

export interface UserDto {
  id: string;
  email: string;
  displayName: string;
  tenantId: string | null;
  twoFactorEnabled: boolean;
  emailConfirmed: boolean;
  /**
   * The user's saved UI locale (e.g. "es-MX"), or null when they've never
   * chosen one — in which case the client keeps using its own detection.
   * Stored on the account, so it follows the user to another device.
   */
  preferredLocale: string | null;
  claims: string[];
}

// Cookie auth: the session cookie must be set on login and sent on every
// subsequent call, so every auth request opts into credentials.
const COOKIE_INIT: RequestInit = { credentials: 'include' };

export async function login(
  email: string,
  password: string,
  rememberMe: boolean,
  signal?: AbortSignal,
): Promise<UserDto> {
  try {
    const { data } = await apiRequest<UserDto>('/v1/auth/login', {
      method: 'POST',
      body: { email, password, rememberMe },
      signal,
      ...COOKIE_INIT,
    });
    return data;
  } catch (err) {
    throw AuthError.from(err);
  }
}

/** Second-factor challenge after a login that returned `mfa-required`. */
export async function verifyMfa(
  code: string,
  rememberMachine: boolean,
  signal?: AbortSignal,
): Promise<void> {
  try {
    await apiRequest<void>('/v1/auth/mfa/verify', {
      method: 'POST',
      body: { code, rememberMachine },
      signal,
      ...COOKIE_INIT,
    });
  } catch (err) {
    throw AuthError.from(err);
  }
}

/**
 * Sends another code for the challenge already in progress.
 *
 * Takes no email address on purpose: the server identifies the half-finished sign-in from its
 * two-factor cookie. Passing an address would make it an open endpoint for mailing codes to
 * anybody. Inside the same time window it is deliberately the same digits, so a slow first email
 * and a resent one both work.
 *
 * Not the only way out of a stuck challenge — see redeemRecoveryCode below — but the first one to
 * try, because it costs nothing and needs no paperwork the user may not have.
 */
export async function resendMfaCode(signal?: AbortSignal): Promise<void> {
  try {
    await apiRequest<void>('/v1/auth/mfa/resend', {
      method: 'POST',
      signal,
      ...COOKIE_INIT,
    });
  } catch (err) {
    throw AuthError.from(err);
  }
}

/**
 * Signs in with a recovery code instead of an emailed one.
 *
 * The way back in when mail is not arriving — which is the whole reason recovery codes exist, and
 * not a hypothetical: on 2026-08-19 a misconfigured SES discarded every message for hours.
 */
export async function redeemRecoveryCode(
  recoveryCode: string,
  signal?: AbortSignal,
): Promise<void> {
  try {
    await apiRequest<void>('/v1/auth/mfa/recovery', {
      method: 'POST',
      body: { recoveryCode },
      signal,
      ...COOKIE_INIT,
    });
  } catch (err) {
    throw AuthError.from(err);
  }
}

/** How many unspent recovery codes this account has. Needs a full session. */
export async function countRecoveryCodes(signal?: AbortSignal): Promise<number> {
  const { data } = await apiRequest<{ remaining: number }>('/v1/auth/mfa/recovery-codes', {
    method: 'GET',
    signal,
    ...COOKIE_INIT,
  });
  return data.remaining;
}

/**
 * Issues a fresh set, replacing any previous one, and returns them.
 *
 * The ONLY time they are readable: the server keeps hashes. Nothing should cache or log the result.
 */
export async function generateRecoveryCodes(signal?: AbortSignal): Promise<string[]> {
  const { data } = await apiRequest<{ recoveryCodes: string[] }>('/v1/auth/mfa/recovery-codes', {
    method: 'POST',
    signal,
    ...COOKIE_INIT,
  });
  return data.recoveryCodes;
}

export async function logout(signal?: AbortSignal): Promise<void> {
  await apiRequest<void>('/v1/auth/logout', { method: 'POST', signal, ...COOKIE_INIT });
}

/**
 * Current user, or `null` when not signed in. A 401 is the normal
 * "anonymous" answer, not an error — callers treat null as logged-out.
 */
export async function getCurrentUser(signal?: AbortSignal): Promise<UserDto | null> {
  try {
    const { data } = await apiRequest<UserDto>('/v1/auth/me', {
      method: 'GET',
      signal,
      ...COOKIE_INIT,
    });
    return data;
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return null;
    throw err;
  }
}

/**
 * The permission codes the current session holds, or an empty list when anonymous.
 *
 * Its own request rather than claims on `/me`, and never cached anywhere: permissions are
 * checked by the API on every call so a tenant admin can grant or revoke one and have it take
 * effect immediately, with no sign-out. Freezing them in the client would put that guarantee
 * back exactly where it was — the server response carries `Cache-Control: no-store`, and the
 * service worker's `/api/` runtime cache excludes this path for the same reason.
 *
 * This list is only ever a UX hint: it decides what to show, never what to allow.
 */
export async function getPermissions(signal?: AbortSignal): Promise<string[]> {
  try {
    const { data } = await apiRequest<{ permissions: string[] }>('/v1/auth/permissions', {
      method: 'GET',
      signal,
      ...COOKIE_INIT,
    });
    return data.permissions;
  } catch (err) {
    // Anonymous is a normal answer, same as /me. Anything else propagates: failing closed with
    // an empty list would silently hide every action and look like a permissions problem.
    if (err instanceof ApiError && err.status === 401) return [];
    throw err;
  }
}

/**
 * Saves the signed-in user's preferences to their account. `preferredLocale`
 * null clears the saved language and hands the choice back to browser
 * detection. Returns the updated profile.
 */
export async function updatePreferences(
  preferredLocale: string | null,
  signal?: AbortSignal,
): Promise<UserDto> {
  const { data } = await apiRequest<UserDto>('/v1/auth/me/preferences', {
    method: 'PATCH',
    body: { preferredLocale },
    signal,
    ...COOKIE_INIT,
  });
  return data;
}

/**
 * Classifies an auth failure so the login screen can show the right message
 * and branch to the MFA step, mirroring RegistrationError in the registration
 * feature. `mfa_required` is not an error the user sees — it drives the
 * second-factor step.
 */
export class AuthError extends Error {
  public readonly status: number;
  public readonly problem: { type?: string; title?: string; detail?: string } | null;

  public constructor(status: number, problem: unknown) {
    const p = (problem ?? {}) as { title?: string; detail?: string; type?: string };
    super(p.detail ?? p.title ?? `HTTP ${status}`);
    this.name = 'AuthError';
    this.status = status;
    this.problem = (problem as AuthError['problem']) ?? null;
  }

  public static from(err: unknown): AuthError {
    if (err instanceof AuthError) return err;
    if (err instanceof ApiError) return new AuthError(err.status, err.problem);
    return new AuthError(0, { title: (err as Error)?.message });
  }

  public get code(): 'mfa_required' | 'locked' | 'invalid' | 'generic' {
    const type = this.problem?.type ?? '';
    if (this.status === 401 && type.endsWith('/mfa-required')) return 'mfa_required';
    if (this.status === 423) return 'locked';
    if (this.status === 401) return 'invalid';
    return 'generic';
  }
}
